// pattern: Imperative Shell
// Upload view — PDF upload with drag-and-drop

import { createElement, addListener, clearElement } from '../shell/dom.ts';
import {
  extractQuestions,
  extractGeneratedQuestions,
  extractFlashcards,
  createQuiz,
} from '../shell/api-client.ts';
import { navigate, routeToHash } from '../shell/router.ts';
import { renderPdfPages } from '../core/pdf-page-renderer.ts';
import { toStoredFlashcard, generateFlashcardId } from '../core/flashcard-model.ts';
import type { ExtractedQuestion } from '../core/question-model.ts';
import type { ExtractedFlashcard } from '../core/flashcard-model.ts';

export function renderUpload(container: HTMLElement, dashboardId: string): () => void {
  const cleanups: Array<() => void> = [];
  let storedQuestions: ReadonlyArray<ExtractedQuestion> = [];
  let extractedDocumentType: string = '';
  let storedFlashcards: ReadonlyArray<ExtractedFlashcard> = [];
  let selectedMode: 'quiz' | 'generate' | 'flashcard' = 'quiz';
  let currentFile: File | null = null;

  // Main container
  const main = createElement('div', { className: 'upload-container upload-wrapper' });

  const title = createElement('h1', { textContent: 'Create New Quiz', className: 'upload-title' });

  // Mode selector
  const modeSelector = createElement('div', { className: 'mode-selector upload-mode-selector' });

  const quizRadio = createElement('input', { type: 'radio', name: 'uploadMode', id: 'mode-quiz' });
  quizRadio.checked = true;
  const quizLabel = createElement('label', {
    className: 'upload-mode-label',
  });
  quizLabel.htmlFor = 'mode-quiz';
  const quizLabelTitle = createElement('span', { textContent: ' Extract Existing Questions' });
  const quizLabelDesc = createElement('small', {
    textContent: 'Upload a quiz or test PDF. AI finds the multiple-choice questions already in it.',
    className: 'upload-mode-desc',
  });
  quizLabel.append(quizLabelTitle, quizLabelDesc);

  const generateRadio = createElement('input', {
    type: 'radio',
    name: 'uploadMode',
    id: 'mode-generate',
  });
  const generateLabel = createElement('label', {
    className: 'upload-mode-label',
  });
  generateLabel.htmlFor = 'mode-generate';
  const generateLabelTitle = createElement('span', {
    textContent: ' Generate Study Questions',
  });
  const generateLabelDesc = createElement('small', {
    textContent:
      'Upload any PDF \u2014 textbook, article, notes. AI creates original multiple-choice questions from the content.',
    className: 'upload-mode-desc',
  });
  generateLabel.append(generateLabelTitle, generateLabelDesc);

  const flashcardRadio = createElement('input', {
    type: 'radio',
    name: 'uploadMode',
    id: 'mode-flashcard',
  });
  const flashcardLabel = createElement('label', {
    className: 'upload-mode-label',
  });
  flashcardLabel.htmlFor = 'mode-flashcard';
  const flashcardLabelTitle = createElement('span', { textContent: ' Flashcards' });
  const flashcardLabelDesc = createElement('small', {
    textContent: 'Upload any PDF. AI creates front/back study cards from the content.',
    className: 'upload-mode-desc',
  });
  flashcardLabel.append(flashcardLabelTitle, flashcardLabelDesc);

  const quizOption = createElement('div');
  quizOption.append(quizRadio, quizLabel);
  const generateOption = createElement('div');
  generateOption.append(generateRadio, generateLabel);
  const flashcardOption = createElement('div');
  flashcardOption.append(flashcardRadio, flashcardLabel);

  modeSelector.append(quizOption, generateOption, flashcardOption);

  const quizRadioCleanup = addListener(quizRadio, 'change', () => {
    selectedMode = 'quiz';
    if (currentFile && (storedFlashcards.length > 0 || storedQuestions.length > 0)) {
      storedFlashcards = [];
      storedQuestions = [];
      showLoading();
      void uploadFile(currentFile);
    }
  });
  cleanups.push(quizRadioCleanup);

  const generateRadioCleanup = addListener(generateRadio, 'change', () => {
    selectedMode = 'generate';
    if (currentFile && (storedQuestions.length > 0 || storedFlashcards.length > 0)) {
      storedQuestions = [];
      storedFlashcards = [];
      showLoading();
      void uploadFile(currentFile);
    }
  });
  cleanups.push(generateRadioCleanup);

  const flashcardRadioCleanup = addListener(flashcardRadio, 'change', () => {
    selectedMode = 'flashcard';
    if (currentFile && (storedQuestions.length > 0 || storedFlashcards.length > 0)) {
      storedQuestions = [];
      storedFlashcards = [];
      showLoading();
      void uploadFile(currentFile);
    }
  });
  cleanups.push(flashcardRadioCleanup);

  // Drop zone
  const dropZone = createElement('div', { className: 'drop-zone' });

  const dropText = createElement('p', {
    textContent: 'Drop a PDF here\nor click to browse',
    className: 'text-secondary upload-drop-text',
  });

  dropZone.appendChild(dropText);

  // Hidden file input
  const fileInput = createElement('input', {
    type: 'file',
    accept: '.pdf',
  });
  fileInput.style.display = 'none';

  // Content area for loading/success/error states
  const contentArea = createElement('div', { className: 'upload-content' });

  main.append(title, modeSelector, dropZone, fileInput, contentArea);
  container.appendChild(main);

  // Event handlers
  const dragoverCleanup = addListener(dropZone, 'dragover', (e) => {
    e.preventDefault();
    dropZone.classList.add('drag-over');
    dropZone.style.borderColor = 'var(--color-primary)';
    dropZone.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
  });
  cleanups.push(dragoverCleanup);

  const dragleaveCleanup = addListener(dropZone, 'dragleave', () => {
    dropZone.classList.remove('drag-over');
    dropZone.style.borderColor = 'var(--color-border)';
    dropZone.style.backgroundColor = '';
  });
  cleanups.push(dragleaveCleanup);

  const dropCleanup = addListener(dropZone, 'drop', (e) => {
    e.preventDefault();
    dropZone.classList.remove('drag-over');
    dropZone.style.borderColor = 'var(--color-border)';
    dropZone.style.backgroundColor = '';

    const files = (e as DragEvent).dataTransfer?.files;
    if (files && files.length > 0) {
      handleFile(files[0]!);
    }
  });
  cleanups.push(dropCleanup);

  const clickCleanup = addListener(dropZone, 'click', () => {
    fileInput.click();
  });
  cleanups.push(clickCleanup);

  const inputCleanup = addListener(fileInput, 'change', () => {
    if (fileInput.files && fileInput.files.length > 0) {
      handleFile(fileInput.files[0]!);
    }
  });
  cleanups.push(inputCleanup);

  function handleFile(file: File): void {
    // Validate PDF
    if (file.type !== 'application/pdf') {
      showError('File must be a PDF');
      return;
    }

    currentFile = file;
    showLoading();
    void uploadFile(file);
  }

  function showLoading(): void {
    clearElement(contentArea);
    const loadingDiv = createElement('div', { className: 'upload-loading' });

    const loadingText = createElement('p', {
      textContent: 'Analyzing document...',
      className: 'upload-loading-text',
    });

    const progressBar = createElement('div', { className: 'progress-bar upload-progress-bar' });

    const progressFill = createElement('div', { className: 'progress-fill upload-progress-fill' });
    progressFill.style.width = '60%';

    progressBar.appendChild(progressFill);
    loadingDiv.append(loadingText, progressBar);
    contentArea.appendChild(loadingDiv);

    // Add CSS animation
    if (!document.getElementById('upload-animations')) {
      const style = createElement('style');
      style.id = 'upload-animations';
      style.textContent = `
        @keyframes progress {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `;
      document.head.appendChild(style);
    }
  }

  function showRenderingFigures(): void {
    clearElement(contentArea);
    const renderDiv = createElement('div', { className: 'upload-loading' });

    const renderText = createElement('p', {
      textContent: 'Rendering figures...',
      className: 'upload-loading-text',
    });

    const progressBar = createElement('div', { className: 'progress-bar upload-progress-bar' });

    const progressFill = createElement('div', { className: 'progress-fill upload-progress-fill' });
    progressFill.style.width = '80%';

    progressBar.appendChild(progressFill);
    renderDiv.append(renderText, progressBar);
    contentArea.appendChild(renderDiv);
  }

  function showError(message: string): void {
    clearElement(contentArea);
    const errorDiv = createElement('div', { className: 'upload-error' });

    const errorMsg = createElement('p', { textContent: message, className: 'upload-error-msg' });

    const tryAgainBtn = createElement('button', {
      className: 'btn btn-secondary upload-btn-full',
      textContent: 'Try Again',
    });

    const tryAgainCleanup = addListener(tryAgainBtn, 'click', () => {
      clearElement(contentArea);
      fileInput.value = '';
      fileInput.click();
    });
    cleanups.push(tryAgainCleanup);

    errorDiv.append(errorMsg, tryAgainBtn);
    contentArea.appendChild(errorDiv);
  }

  function showSuccess(filename: string): void {
    clearElement(contentArea);
    const successDiv = createElement('div', { className: 'upload-success' });

    const docTypeEl = createElement('p', {
      textContent: `Document type: ${extractedDocumentType}`,
      className: 'upload-doc-type',
    });

    const isFlashcard = selectedMode === 'flashcard';
    const count = isFlashcard ? storedFlashcards.length : storedQuestions.length;
    const figureCount = isFlashcard
      ? storedFlashcards.filter((f) => f.figureDataUrl).length
      : storedQuestions.filter((q) => q.figureDataUrl).length;
    const itemName = isFlashcard ? 'flashcards' : 'questions';
    const verb = selectedMode === 'generate' ? 'Generated' : 'Found';
    const countText =
      figureCount > 0
        ? `${verb} ${count} ${itemName} (${figureCount} with figures)`
        : `${verb} ${count} ${itemName}`;

    const countEl = createElement('p', {
      textContent: countText,
      className: 'text-secondary upload-count',
    });

    const reviewBtn = createElement('button', {
      className: 'btn btn-primary upload-btn-full',
      textContent: isFlashcard ? 'Review Flashcards →' : 'Review Questions →',
    });

    const reviewCleanup = addListener(reviewBtn, 'click', () => {
      void createAndNavigate(filename);
    });
    cleanups.push(reviewCleanup);

    successDiv.append(docTypeEl, countEl, reviewBtn);
    contentArea.appendChild(successDiv);
  }

  async function uploadFile(file: File): Promise<void> {
    if (selectedMode === 'flashcard') {
      const result = await extractFlashcards(file);

      if (!result.ok) {
        showError(result.error || 'Extraction failed');
        return;
      }

      let flashcards = result.data.flashcards;

      // Render figures for flashcards that reference them
      const figurePages = flashcards
        .filter((f) => f.figurePageNumber != null)
        .map((f) => f.figurePageNumber!);

      if (figurePages.length > 0) {
        showRenderingFigures();
        const pdfBuffer = await file.arrayBuffer();
        const renderResult = await renderPdfPages(pdfBuffer, figurePages);

        flashcards = flashcards.map((f) => {
          if (f.figurePageNumber == null) return f;
          const dataUrl = renderResult.rendered.get(f.figurePageNumber);
          if (!dataUrl) return f;
          return { ...f, figureDataUrl: dataUrl };
        });
      }

      storedFlashcards = flashcards;
      extractedDocumentType = formatDocumentType(result.data.documentType);
      showSuccess(file.name);
    } else {
      const result =
        selectedMode === 'generate'
          ? await extractGeneratedQuestions(file)
          : await extractQuestions(file);

      if (!result.ok) {
        showError(result.error || 'Extraction failed');
        return;
      }

      let questions = result.data.questions;

      const figurePages = questions
        .filter((q) => q.figurePageNumber != null)
        .map((q) => q.figurePageNumber!);

      if (figurePages.length > 0) {
        showRenderingFigures();
        const pdfBuffer = await file.arrayBuffer();
        const renderResult = await renderPdfPages(pdfBuffer, figurePages);

        questions = questions.map((q) => {
          if (q.figurePageNumber == null) return q;
          const dataUrl = renderResult.rendered.get(q.figurePageNumber);
          if (!dataUrl) return q;
          return { ...q, figureDataUrl: dataUrl };
        });
      }

      storedQuestions = questions;
      extractedDocumentType = formatDocumentType(result.data.documentType);
      showSuccess(file.name);
    }
  }

  async function createAndNavigate(filename: string): Promise<void> {
    const quizName = filename
      .replace(/\.pdf$/i, '')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

    if (selectedMode === 'flashcard') {
      const createResult = await createQuiz({
        dashboardId,
        name: quizName,
        questions: [],
        flashcards: storedFlashcards.map((f) => toStoredFlashcard(f, generateFlashcardId())),
        mode: 'flashcard',
      });

      if (createResult.ok) {
        navigate(routeToHash({ type: 'editor', dashboardId, quizId: createResult.data.id }));
      } else {
        showError(createResult.error || 'Failed to create flashcard deck');
      }
    } else {
      const createResult = await createQuiz({
        dashboardId,
        name: quizName,
        questions: storedQuestions,
        mode: 'structured',
      });

      if (createResult.ok) {
        navigate(routeToHash({ type: 'editor', dashboardId, quizId: createResult.data.id }));
      } else {
        showError(createResult.error || 'Failed to create quiz');
      }
    }
  }

  return () => cleanups.forEach((fn) => fn());
}

function formatDocumentType(docType: string): string {
  switch (docType) {
    case 'quiz_or_test':
      return 'Quiz or Test';
    case 'educational_content':
      return 'Educational Content';
    case 'mixed':
      return 'Mixed Content';
    case 'other':
      return 'Other';
    default:
      return docType;
  }
}
